import { triggerBlobDownload } from "@/modules/reports/utils/download";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { ModelConversion } from "@repo/schema";
import { Download } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyGetConversionLogQuery } from "../../services";
import { LogConsole } from "../LogConsole/LogConsole";

export interface ConversionLogDialogProps {
  projectId: number;
  modelId: number;
  conversion: ModelConversion;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 2000;

/**
 * `<ISO timestamp> <LEVEL> <source>  <message>`, the format the job writes.
 *
 * Deliberately tolerant: a line that does not match is rendered verbatim as a
 * continuation rather than dropped, so a vendor tool that emits something
 * unexpected still reaches the reader.
 */
const LINE = /^(\S+) (INFO|DEBUG|ERROR) +(\S+) +([\s\S]*)$/;

const LEVEL_CLASS: Record<string, string> = {
  INFO: "text-pear-500",
  DEBUG: "text-white/60",
  ERROR: "text-red-500",
};

export const ConversionLogDialog = ({
  projectId,
  modelId,
  conversion,
  onClose,
}: ConversionLogDialogProps) => {
  const { t } = useTranslation(["model", "common"]);
  const [text, setText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [fetchChunk] = useLazyGetConversionLogQuery();

  // Refs rather than state: the poll loop reads these every tick, and putting
  // them in the effect's dependencies would tear down and restart the interval
  // on every chunk that arrives.
  const offsetRef = useRef(0);
  const completeRef = useRef(false);

  const conversionId = conversion.id;
  // A retry writes to a different object, so both the accumulated text and the
  // byte cursor have to be thrown away — otherwise the next slice would be read
  // from the new log at the old log's offset.
  const attempt = conversion.attemptCount;

  useEffect(() => {
    let cancelled = false;

    offsetRef.current = 0;
    completeRef.current = false;
    setText("");
    setIsLoading(true);

    const poll = async () => {
      try {
        const chunk = await fetchChunk({
          projectId,
          modelId,
          conversionId,
          after: offsetRef.current,
        }).unwrap();

        if (cancelled) return;

        offsetRef.current = chunk.offset;
        completeRef.current = chunk.complete;
        if (chunk.content) setText((current) => current + chunk.content);
      } catch {
        // Transient — the next tick retries. A hard failure would otherwise
        // clear a log the user is still reading.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void poll();

    const interval = setInterval(() => {
      // One last read after the conversion goes terminal: the job flushes its
      // closing lines just before the webhook, so `complete` can arrive on the
      // same response that is still missing them.
      if (completeRef.current) {
        clearInterval(interval);
        void poll();
        return;
      }
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [fetchChunk, projectId, modelId, conversionId, attempt]);

  const download = useCallback(() => {
    triggerBlobDownload(
      new Blob([text], { type: "text/plain" }),
      `conversion-${conversionId}-${conversion.target}-${conversion.precision}.log`.toLowerCase(),
    );
  }, [text, conversionId, conversion.target, conversion.precision]);

  const lines = text ? text.replace(/\n$/, "").split("\n") : [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[80vh] flex-col sm:max-w-4xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4 pr-6">
            <div className="flex flex-col gap-1">
              <DialogTitle>
                {t("conversionLog.title", {
                  target: conversion.target,
                  precision: conversion.precision,
                })}
              </DialogTitle>
              <DialogDescription>{t("conversionLog.description")}</DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              disabled={lines.length === 0}
              onClick={download}
            >
              <Download className="size-4" />
              {t("common:actions.download")}
            </Button>
          </div>
        </DialogHeader>

        <LogConsole
          scrollKey={text}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          {lines.map((line, index) => {
            const parsed = LINE.exec(line);

            if (!parsed) {
              return (
                <p
                  key={index}
                  className="font-mono text-sm break-all whitespace-pre-wrap text-white/60"
                >
                  {line}
                </p>
              );
            }

            const [, timestamp, level, source, message] = parsed;

            return (
              <p
                key={index}
                className={`font-mono text-sm break-all whitespace-pre-wrap ${LEVEL_CLASS[level]}`}
              >
                <span className="text-gray-400">
                  [{timestamp.slice(11, 23)}]
                </span>{" "}
                <span className="text-blue-200">{source}</span>{" "}
                <span>{message}</span>
              </p>
            );
          })}

          {lines.length === 0 && (
            <p className="font-mono text-sm text-white/40">
              {isLoading ? t("conversionLog.loading") : t("conversionLog.empty")}
            </p>
          )}
        </LogConsole>
      </DialogContent>
    </Dialog>
  );
};
